import { useEffect, useMemo, useState } from 'react'
import {
  subscribeToAutomations,
  subscribeToAutomationGraphs,
  subscribeToAutomationGraphRuns,
  subscribeToAutomationRuns,
} from '../services/automationService'
import type { AutomationDefinition, AutomationGraph, AutomationRun } from '../types/automation'
import { getDemoWorkspaceData, ARIA_AND_CO_DEMO_DATA } from '../explore_seeds/ariaAndCoDemoData'

const EMPTY_AUTOMATIONS: AutomationDefinition[] = []
const EMPTY_GRAPHS: AutomationGraph[] = []
const EMPTY_RUNS: AutomationRun[] = []

export const useAutomations = (workspaceId: string | null) => {
  const demo = workspaceId ? getDemoWorkspaceData(workspaceId) : null
  const [subscribedState, setSubscribedState] = useState<{
    workspaceId: string
    items: AutomationDefinition[]
    error: string | null
  } | null>(null)

  const items = useMemo(() => {
    if (!workspaceId) return EMPTY_AUTOMATIONS
    if (demo) return demo.automations
    if (subscribedState?.workspaceId === workspaceId) return subscribedState.items
    return EMPTY_AUTOMATIONS
  }, [workspaceId, demo, subscribedState])

  const loading = Boolean(workspaceId && !demo && subscribedState?.workspaceId !== workspaceId)
  const error = (!workspaceId || demo || subscribedState?.workspaceId !== workspaceId) ? null : subscribedState.error

  useEffect(() => {
    if (!workspaceId || demo) {
      return
    }
    const currentWorkspaceId = workspaceId
    const unsubscribe = subscribeToAutomations(
      currentWorkspaceId,
      (next) => {
        setSubscribedState({ workspaceId: currentWorkspaceId, items: next, error: null })
      },
      (err) => {
        setSubscribedState({ workspaceId: currentWorkspaceId, items: EMPTY_AUTOMATIONS, error: err.message })
      },
    )
    return () => unsubscribe()
  }, [workspaceId, demo])

  return { items, loading, error }
}

export const useAutomationGraphs = (workspaceId: string | null) => {
  const demo = workspaceId ? getDemoWorkspaceData(workspaceId) : null
  const [subscribedState, setSubscribedState] = useState<{
    workspaceId: string
    items: AutomationGraph[]
    error: string | null
  } | null>(null)

  const items = useMemo(() => {
    if (!workspaceId || demo) return EMPTY_GRAPHS
    if (subscribedState?.workspaceId === workspaceId) return subscribedState.items
    return EMPTY_GRAPHS
  }, [workspaceId, demo, subscribedState])

  const loading = Boolean(workspaceId && !demo && subscribedState?.workspaceId !== workspaceId)
  const error = (!workspaceId || demo || subscribedState?.workspaceId !== workspaceId) ? null : subscribedState.error

  useEffect(() => {
    if (!workspaceId || demo) {
      return
    }
    const currentWorkspaceId = workspaceId
    const unsubscribe = subscribeToAutomationGraphs(
      currentWorkspaceId,
      (next) => {
        setSubscribedState({ workspaceId: currentWorkspaceId, items: next, error: null })
      },
      (err) => {
        setSubscribedState({ workspaceId: currentWorkspaceId, items: EMPTY_GRAPHS, error: err.message })
      },
    )
    return () => unsubscribe()
  }, [workspaceId, demo])

  return { items, loading, error }
}

export const useAutomationRuns = (automationId: string | null) => {
  const isAriaDemo = useMemo(
    () => Boolean(automationId && ARIA_AND_CO_DEMO_DATA.automations.some((auto) => auto.id === automationId)),
    [automationId],
  )
  const demoRuns = useMemo(() => {
    if (!isAriaDemo || !automationId) return EMPTY_RUNS
    return ARIA_AND_CO_DEMO_DATA.automationRuns.filter((run) => run.automationId === automationId)
  }, [isAriaDemo, automationId])

  const [subscribedState, setSubscribedState] = useState<{
    automationId: string
    runs: AutomationRun[]
  } | null>(null)

  const runs = useMemo(() => {
    if (!automationId) return EMPTY_RUNS
    if (isAriaDemo) return demoRuns
    if (subscribedState?.automationId === automationId) return subscribedState.runs
    return EMPTY_RUNS
  }, [automationId, isAriaDemo, demoRuns, subscribedState])

  const loading = Boolean(automationId && !isAriaDemo && subscribedState?.automationId !== automationId)

  useEffect(() => {
    if (!automationId || isAriaDemo) {
      return
    }
    const currentAutomationId = automationId
    const unsubscribe = subscribeToAutomationRuns(currentAutomationId, (next) => {
      setSubscribedState({ automationId: currentAutomationId, runs: next })
    })
    return () => unsubscribe()
  }, [automationId, isAriaDemo])

  return { runs, loading }
}

export const useAutomationGraphRuns = (graphId: string | null) => {
  const [subscribedState, setSubscribedState] = useState<{
    graphId: string
    runs: AutomationRun[]
  } | null>(null)

  const runs = useMemo(() => {
    if (!graphId) return EMPTY_RUNS
    if (subscribedState?.graphId === graphId) return subscribedState.runs
    return EMPTY_RUNS
  }, [graphId, subscribedState])

  const loading = Boolean(graphId && subscribedState?.graphId !== graphId)

  useEffect(() => {
    if (!graphId) {
      return
    }
    const currentGraphId = graphId
    const unsubscribe = subscribeToAutomationGraphRuns(currentGraphId, (next) => {
      setSubscribedState({ graphId: currentGraphId, runs: next })
    })
    return () => unsubscribe()
  }, [graphId])

  return { runs, loading }
}
